import { useState, useEffect, useCallback } from 'react';
import { ConsentItemProps } from '../ConsentItem/ConsentItem';

const createInitialConsentStates = (items: ConsentItemProps[]): Record<string, boolean> => {
  const initialStates: Record<string, boolean> = {};
  items.forEach(item => {
    initialStates[item.id] = item.checked || false;
  });
  return initialStates;
};

export interface UseConsentGroupOptions {
  items: ConsentItemProps[];
  hasAcceptAllOption?: boolean;
  onAcceptAllChange?: (checked: boolean) => void;
}

export const useConsentGroup = ({ items, hasAcceptAllOption = false, onAcceptAllChange }: UseConsentGroupOptions) => {
  const [consentStates, setConsentStates] = useState<Record<string, boolean>>(() =>
    createInitialConsentStates(items)
  );
  const [acceptAllChecked, setAcceptAllChecked] = useState<boolean>(false);

  const handleConsentChange = useCallback((id: string, checked: boolean) => {
    setConsentStates(prev => ({
      ...prev,
      [id]: checked
    }));

    const originalItem = items.find(item => item.id === id);
    if (originalItem?.onChange) {
      originalItem.onChange(checked);
    }
  }, [items]);

  const handleAcceptAllChange = useCallback((checked: boolean) => {
    setConsentStates(prevStates => {
      const newStates: Record<string, boolean> = {};

      items.forEach(item => {
        newStates[item.id] = item.disabled ? prevStates[item.id] : checked;
      });

      return newStates;
    });

    if (onAcceptAllChange) {
      onAcceptAllChange(checked);
    } else {
      items.forEach(item => {
        if (!item.disabled && item.onChange) {
          item.onChange(checked);
        }
      });
    }
  }, [items, onAcceptAllChange]);

  // Synchronize "accept all" checkbox state
  useEffect(() => {
    if (hasAcceptAllOption) {
      // Consider current consent states for all items (including disabled ones).
      // If every item is checked, keep "accept all" checked even when items become disabled
      // (e.g., during submission) so the control reflects the actual state.
      const allChecked = items.length > 0 && items.every(item => consentStates[item.id]);
      setAcceptAllChecked(allChecked);
    }
  }, [consentStates, items, hasAcceptAllOption]);

  return {
    consentStates,
    acceptAllChecked,
    handleConsentChange,
    handleAcceptAllChange,
  };
};
